import {
    Builder,
    G2_KINDS,
    G3_KINDS,
    G4_KINDS,
    G5_FAMILIES,
    G6_FAMILIES,
    G7_FAMILIES,
    G8_FAMILIES,
    OpKind,
} from "./candidates.ts";
import { Rng } from "./rng.ts";

/*
 * What each grade's daily board is made of (ADR 0127 §9): the range and window
 * of its four values, how its groups are written, and how often a board has
 * negative values. The table is data; equalGroups.ts is what turns it into a board.
 */

export type Level = "g2" | "g3" | "g4" | "g5" | "g6" | "g7" | "g8";
export const LEVELS: Level[] = ["g2", "g3", "g4", "g5", "g6", "g7", "g8"];

/** The grade a new visitor starts on. */
export const DEFAULT_LEVEL: Level = "g3";

export const gradeOf = (level: Level): number => Number(level.slice(1));

export interface GradeSpec {
    /** The four values lie in lo to hi, in a window of minWidth to maxWidth whole numbers. */
    lo: number;
    hi: number;
    minWidth: number;
    maxWidth: number;
    /** 0: never a negative value. N: one board in N has one to three of them. */
    negativeOneIn: number;
    /** The ways to write a tile, by key: an operation (grades 2 to 4) or a family (5 to 8). */
    kinds: Record<string, Builder>;
    /** Grades 2 to 4: the four keys a group is written with. `rng` makes the one choice left. */
    opsFor?: (value: number, rng: Rng) => string[];
    /** Grades 5 to 8: the keys a value may be written with; four are drawn for each group. */
    familiesFor?: (value: number) => string[];
    /** A window's four values are kept only if this holds. */
    suitable?: (values: number[]) => boolean;
}

const keysOf = (kinds: Record<string, Builder>): string[] => Object.keys(kinds);

/** Grades 3 and 4: one of each operation; with no times fact, a second sum or difference in its place. */
function operationsFor(kinds: Record<OpKind, Builder>) {
    return (value: number, rng: Rng): string[] =>
        kinds["×"](value).length > 0 ? ["+", "−", "×", "÷"] : ["+", "−", "÷", rng.pick<string>(["+", "−"])];
}

/** At least three of the four values have a times fact to write. */
const mostHaveTimes =
    (kinds: Record<OpKind, Builder>) =>
    (values: number[]): boolean =>
        values.filter((value) => kinds["×"](value).length > 0).length >= 3;

const NEGATIVE_FAMILIES = ["a", "b", "e", "f"];

export const GRADES: Record<Level, GradeSpec> = {
    // Differences stop at 18: nothing above 20 is written, so 19 and 20 have no take-away.
    g2: {
        lo: 6,
        hi: 18,
        minWidth: 6,
        maxWidth: 8,
        negativeOneIn: 0,
        kinds: G2_KINDS,
        opsFor: (value) => (value === 18 ? ["+", "+", "+", "−"] : ["+", "+", "−", "−"]),
    },
    g3: {
        lo: 6,
        hi: 48,
        minWidth: 8,
        maxWidth: 12,
        negativeOneIn: 0,
        kinds: G3_KINDS,
        opsFor: operationsFor(G3_KINDS),
        suitable: mostHaveTimes(G3_KINDS),
    },
    g4: {
        lo: 24,
        hi: 120,
        minWidth: 10,
        maxWidth: 15,
        negativeOneIn: 0,
        kinds: G4_KINDS,
        opsFor: operationsFor(G4_KINDS),
        suitable: mostHaveTimes(G4_KINDS),
    },
    g5: {
        lo: 10,
        hi: 80,
        minWidth: 10,
        maxWidth: 15,
        negativeOneIn: 0,
        kinds: G5_FAMILIES,
        familiesFor: () => keysOf(G5_FAMILIES),
    },
    g6: {
        lo: 6,
        hi: 60,
        minWidth: 12,
        maxWidth: 15,
        negativeOneIn: 0,
        kinds: G6_FAMILIES,
        familiesFor: () => keysOf(G6_FAMILIES),
    },
    g7: {
        lo: -20,
        hi: 40,
        minWidth: 12,
        maxWidth: 15,
        negativeOneIn: 2,
        kinds: G7_FAMILIES,
        familiesFor: (value) => (value < 0 ? NEGATIVE_FAMILIES : keysOf(G7_FAMILIES)),
    },
    g8: {
        lo: -20,
        hi: 60,
        minWidth: 12,
        maxWidth: 15,
        negativeOneIn: 3,
        kinds: G8_FAMILIES,
        familiesFor: (value) => (value < 0 ? [...NEGATIVE_FAMILIES, "h"] : keysOf(G8_FAMILIES)),
    },
};
